import * as React from 'react';
import { Calendar as CalendarIcon, ChevronDown, ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button, buttonVariants } from '@/components/ui/button';
import { Popover } from '@/components/ui/popover';
import { Tooltip } from '@/components/ui/tooltip';

interface DatePickerProps {
  value: string; // YYYY-MM-DD
  onChange: (next: string) => void;
  /** Optional formatter for the trigger label. Defaults to a short locale string. */
  formatLabel?: (iso: string) => string;
  className?: string;
  /** Extra classes applied to the trigger button (e.g. to flatten it inside a segmented group). */
  triggerClassName?: string;
  ariaLabel?: string;
  locale?: string;
  /** Week picker: rows highlight and select as WHOLE weeks (Mon–Sun). The
   *  clicked day is still what lands in onChange — callers snap to Monday. */
  weekMode?: boolean;
}

function isoToParts(iso: string): { y: number; m: number; d: number } {
  const [y, m, d] = iso.split('-').map(Number);
  return { y, m, d };
}

function partsToIso(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function defaultLabel(iso: string, locale = 'ru-RU'): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(locale, {
    day: 'numeric', month: 'short', year: 'numeric',
  });
}

/**
 * Token-styled date picker: a button trigger that opens a calendar popover.
 * Replaces the browser's default `<input type="date">` UI which carries
 * platform-specific calendar chrome that doesn't match our design system.
 */
export function DatePicker({
  value,
  onChange,
  formatLabel,
  className,
  triggerClassName,
  ariaLabel,
  locale = 'ru-RU',
  weekMode = false,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);

  const { y: vy, m: vm, d: vd } = isoToParts(value);
  const [viewYear, setViewYear] = React.useState(vy);
  const [viewMonth, setViewMonth] = React.useState(vm); // 1-based
  React.useEffect(() => {
    setViewYear(vy);
    setViewMonth(vm);
  }, [vy, vm]);

  const label = formatLabel ? formatLabel(value) : defaultLabel(value, locale);

  return (
    <div className={cn('relative inline-block', className)}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-label={ariaLabel}
        aria-expanded={open}
        className={cn(
          buttonVariants({ variant: 'outline', size: 'sm' }),
          // bg-card so the trigger reads as a control, not part of the page.
          'h-9 w-full justify-start gap-2 bg-card px-3 text-sm font-normal',
          'tabular-nums',
          triggerClassName,
        )}
      >
        <CalendarIcon className="size-4 text-muted-foreground" />
        <span className="truncate">{label}</span>
      </button>

      {/* Portal-rendered popover so the calendar escapes any `overflow-hidden`
          ancestor (e.g. a Card) instead of being clipped by it. */}
      <Popover open={open} onOpenChange={setOpen} triggerRef={triggerRef} side="bottom" align="start">
        <div className="w-72 p-3" role="dialog">
          <CalendarBody
            viewYear={viewYear}
            viewMonth={viewMonth}
            onPrevMonth={() => {
              const nm = viewMonth === 1 ? 12 : viewMonth - 1;
              const ny = viewMonth === 1 ? viewYear - 1 : viewYear;
              setViewMonth(nm);
              setViewYear(ny);
            }}
            onNextMonth={() => {
              const nm = viewMonth === 12 ? 1 : viewMonth + 1;
              const ny = viewMonth === 12 ? viewYear + 1 : viewYear;
              setViewMonth(nm);
              setViewYear(ny);
            }}
            selectedYear={vy}
            selectedMonth={vm}
            selectedDay={vd}
            locale={locale}
            weekMode={weekMode}
            onPick={(y, m, d) => {
              onChange(partsToIso(y, m, d));
              setOpen(false);
            }}
          />
        </div>
      </Popover>
    </div>
  );
}

export interface DateRangePreset {
  label: string;
  from: string;
  to: string;
  /** Рекомендованный для раздела период — помечается звёздочкой (Алексей 17.09). */
  recommended?: boolean;
}

/**
 * Classic range picker: one calendar, first click = start, second = end
 * (swapped if reversed), with small preset chips underneath.
 */
export function DateRangePicker({
  from,
  to,
  onChange,
  locale = 'ru-RU',
  max,
  presets,
  className,
  triggerClassName,
  ariaLabel,
  compact = false,
  labelSuffix,
  labelOverride,
}: {
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
  /** Appended to the trigger label, e.g. a sub-day time window «10:00–12:00». */
  labelSuffix?: string;
  /** Replaces the whole trigger label — a sub-period picked on a chart reads
   *  «1 окт 08:00 – 8 окт 12:37» instead of dates + a separate clock suffix. */
  labelOverride?: string;
  locale?: string;
  /** Latest pickable day (YYYY-MM-DD). */
  max?: string;
  presets?: DateRangePreset[];
  className?: string;
  triggerClassName?: string;
  ariaLabel?: string;
  /** Иконка-кнопка без подписи — для мобильной шапки, где текст не помещается. */
  compact?: boolean;
}) {
  const [open, setOpen] = React.useState(false);
  const [pending, setPending] = React.useState<string | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);

  const { y: vy, m: vm } = isoToParts(to);
  const [viewYear, setViewYear] = React.useState(vy);
  const [viewMonth, setViewMonth] = React.useState(vm);
  React.useEffect(() => {
    setViewYear(vy);
    setViewMonth(vm);
  }, [vy, vm]);
  React.useEffect(() => {
    if (!open) setPending(null);
  }, [open]);

  const fmt = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(locale, { day: 'numeric', month: 'short' });
  };

  // When the selected range exactly matches a named preset (Сегодня / Вчера / …),
  // show that label instead of "21 июл — 21 июл" (Alexey 2026-07-21).
  const matchedPreset = presets?.find((p) => p.from === from && p.to === to);
  const triggerLabel =
    labelOverride ??
    (matchedPreset?.label ?? `${fmt(from)} — ${fmt(to)}`) + (labelSuffix ? ` · ${labelSuffix}` : '');
  // Не жёлтая: жёлтые звёзды — это оценки гостей; здесь наш акцент (Алексей 17.09, AI17.5).
  const star = <Star className="size-3 text-primary" aria-hidden />;

  const pick = (y: number, m: number, d: number) => {
    const iso = partsToIso(y, m, d);
    if (max && iso > max) return;
    if (!pending) {
      setPending(iso);
      return;
    }
    const [a, b] = [pending, iso].sort();
    onChange(a, b);
    setPending(null);
    setOpen(false);
  };

  return (
    <div className={cn('relative inline-block', className)}>
      <Tooltip content={compact ? triggerLabel : undefined}><button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={ariaLabel}
        aria-expanded={open}
        className={cn(
          buttonVariants({ variant: 'outline', size: 'sm' }),
          compact
            ? 'size-9 justify-center rounded-full bg-card p-0 shadow-sm transition-colors hover:border-primary/40 hover:bg-muted/60'
            : 'h-9 justify-start gap-2 rounded-full bg-card px-3.5 text-sm font-medium tabular-nums shadow-sm transition-colors hover:border-primary/40 hover:bg-muted/60',
          open && 'border-primary/50 bg-muted/60',
          triggerClassName,
        )}
      >
        <CalendarIcon className="size-4 text-muted-foreground" />
        {!compact && <span className="flex items-center gap-1 truncate">{matchedPreset?.recommended && star}{triggerLabel}</span>}
        {!compact && <ChevronDown className="size-4 shrink-0 text-muted-foreground" />}
      </button></Tooltip>

      <Popover open={open} onOpenChange={setOpen} triggerRef={triggerRef} side="bottom" align="start">
        <div className="w-72 p-3" role="dialog">
          <CalendarBody
            viewYear={viewYear}
            viewMonth={viewMonth}
            onPrevMonth={() => {
              const nm = viewMonth === 1 ? 12 : viewMonth - 1;
              const ny = viewMonth === 1 ? viewYear - 1 : viewYear;
              setViewMonth(nm);
              setViewYear(ny);
            }}
            onNextMonth={() => {
              const nm = viewMonth === 12 ? 1 : viewMonth + 1;
              const ny = viewMonth === 12 ? viewYear + 1 : viewYear;
              setViewMonth(nm);
              setViewYear(ny);
            }}
            selectedYear={vy}
            selectedMonth={vm}
            selectedDay={isoToParts(to).d}
            locale={locale}
            rangeFrom={pending ?? from}
            rangeTo={pending ?? to}
            onPick={pick}
          />
          {presets && presets.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5 border-t border-border pt-2">
              {presets.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => {
                    onChange(p.from, p.to);
                    setPending(null);
                    setOpen(false);
                  }}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-medium transition-colors',
                    p.from === from && p.to === to
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border text-muted-foreground hover:text-foreground',
                  )}
                >
                  {/* Выбранная кнопка синяя — звёздочка на ней белая, иначе теряется (#3255) */}
                  {p.recommended && (p.from === from && p.to === to ? <Star className="size-3 text-primary-foreground" aria-hidden /> : star)}
                  {p.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </Popover>
    </div>
  );
}

function CalendarBody({
  viewYear,
  viewMonth,
  onPrevMonth,
  onNextMonth,
  selectedYear,
  selectedMonth,
  selectedDay,
  onPick,
  locale,
  weekMode = false,
  rangeFrom,
  rangeTo,
}: {
  viewYear: number;
  viewMonth: number;
  onPrevMonth: () => void;
  onNextMonth: () => void;
  selectedYear: number;
  selectedMonth: number;
  selectedDay: number;
  onPick: (y: number, m: number, d: number) => void;
  locale: string;
  weekMode?: boolean;
  /** Range mode (both set): days in [rangeFrom, rangeTo] highlight as a band,
   *  endpoints solid. Overrides the single-day selection styling. */
  rangeFrom?: string;
  rangeTo?: string;
}) {
  const firstOfMonth = new Date(viewYear, viewMonth - 1, 1);
  // Weekday with Monday as 0 (matches RU/EN business calendars better than Sunday-first).
  const firstWeekday = (firstOfMonth.getDay() + 6) % 7;
  const daysInMonth = new Date(viewYear, viewMonth, 0).getDate();

  const today = new Date();
  const isTodayCell = (d: number) =>
    today.getFullYear() === viewYear &&
    today.getMonth() + 1 === viewMonth &&
    today.getDate() === d;

  const monthLabel = new Date(viewYear, viewMonth - 1, 1).toLocaleDateString(locale, {
    month: 'long', year: 'numeric',
  });

  // Localized weekday labels (Mon-Sun). We use a known Monday (2024-01-01) to derive them.
  const weekdayLabels = Array.from({ length: 7 }).map((_, i) =>
    new Date(2024, 0, 1 + i).toLocaleDateString(locale, { weekday: 'short' }),
  );

  const cells: Array<number | null> = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7) cells.push(null);
  // Week rows (Mon-Sun). In weekMode a row hovers/selects as one unit.
  const weekRows: Array<Array<number | null>> = [];
  for (let i = 0; i < cells.length; i += 7) weekRows.push(cells.slice(i, i + 7));

  // The selected date's Mon-Sun window (weekMode highlight).
  const selDate = new Date(selectedYear, selectedMonth - 1, selectedDay);
  const selMon = new Date(selDate);
  selMon.setDate(selDate.getDate() - ((selDate.getDay() + 6) % 7));
  selMon.setHours(0, 0, 0, 0);
  const selSun = new Date(selMon);
  selSun.setDate(selMon.getDate() + 6);
  selSun.setHours(23, 59, 59, 999);
  const inSelectedWeek = (d: number) => {
    const dt = new Date(viewYear, viewMonth - 1, d);
    return dt >= selMon && dt <= selSun;
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onPrevMonth}
          aria-label="prev month"
        >
          <ChevronLeft className="size-4" />
        </Button>
        <div className="text-sm font-medium capitalize">{monthLabel}</div>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onNextMonth}
          aria-label="next month"
        >
          <ChevronRight className="size-4" />
        </Button>
      </div>
      <div className="mb-1 grid grid-cols-7 gap-0.5">
        {weekdayLabels.map(w => (
          <div
            key={w}
            className="text-center text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
          >
            {w}
          </div>
        ))}
      </div>
      <div className="space-y-0.5">
        {weekRows.map((row, ri) => (
          <div
            key={ri}
            className={cn(
              'grid grid-cols-7',
              weekMode ? 'group gap-0 overflow-hidden rounded-md' : 'gap-0.5',
            )}
          >
            {row.map((d, ci) => {
              if (d === null)
                return (
                  <div key={`p-${ci}`} className={cn('h-9', weekMode && 'group-hover:bg-muted')} />
                );
              const iso = partsToIso(viewYear, viewMonth, d);
              const rangeActive = !!rangeFrom && !!rangeTo;
              const isRangeEndpoint = rangeActive && (iso === rangeFrom || iso === rangeTo);
              const isInRange = rangeActive && iso >= rangeFrom! && iso <= rangeTo!;
              const isSelected =
                !weekMode &&
                !rangeActive &&
                selectedYear === viewYear &&
                selectedMonth === viewMonth &&
                selectedDay === d;
              const isWeekSelected = weekMode && inSelectedWeek(d);
              const isToday = isTodayCell(d);
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => onPick(viewYear, viewMonth, d)}
                  className={cn(
                    'inline-flex h-9 items-center justify-center text-sm tabular-nums transition-colors',
                    weekMode ? 'rounded-none group-hover:bg-muted' : 'size-9 rounded-md hover:bg-muted',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
                    isSelected || isRangeEndpoint
                      ? 'bg-primary text-primary-foreground hover:bg-primary'
                      : isInRange
                        ? 'bg-primary/20 text-foreground'
                        : isWeekSelected
                          ? 'bg-primary/20 text-foreground group-hover:bg-primary/25'
                          : isToday
                            ? 'border border-primary/40 text-primary'
                            : 'text-foreground',
                  )}
                  aria-pressed={isSelected || isWeekSelected || isRangeEndpoint}
                >
                  {d}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
